import { useTranslation } from 'react-i18next';
import { ContentLanguageCode, DEFAULT_LANGUAGE } from '@/shared/constants';
import { getMultilingualValue, getMultilingualValueForLang } from '@/shared/utils/languageUtils';
import { Museum, MuseumArea, Work } from '@/shared/types';
import chevronIcon from '@/assets/svg/chevron_icon.svg';
import draftIcon from '@/assets/svg/draft_icon.svg';
import headphoneIcon from '@/assets/svg/headphone_icon.svg';
import checkIconCircle from '@/assets/svg/check_icon_circle.svg';
import trashIcon from '@/assets/svg/trash_icon.svg';
import { WorkDropdown } from '../hooks/useCreateWorkForm';

interface WorkMetadataFieldsProps {
  isEditMode: boolean;
  museum: Museum | null;
  currentLanguage: ContentLanguageCode;
  activeLanguages: ContentLanguageCode[];
  title: Record<ContentLanguageCode, string>;
  setTitle: (title: Record<ContentLanguageCode, string>) => void;
  statusByLanguage: Record<string, 'draft' | 'published'>;
  setStatusByLanguage: (status: Record<string, 'draft' | 'published'>) => void;
  floor: Record<ContentLanguageCode, string>;
  setFloor: (floor: Record<ContentLanguageCode, string>) => void;
  room: Record<ContentLanguageCode, string>;
  setRoom: (room: Record<ContentLanguageCode, string>) => void;
  pillNumber: string;
  setPillNumber: (value: string) => void;
  order: number;
  setOrder: (order: number) => void;
  allWorks: Work[];
  statusDropdownOpen: boolean;
  setStatusDropdownOpen: (open: boolean) => void;
  orderDropdownOpen: boolean;
  setOrderDropdownOpen: (open: boolean) => void;
  floorDropdownOpen: boolean;
  setFloorDropdownOpen: (open: boolean) => void;
  roomDropdownOpen: boolean;
  setRoomDropdownOpen: (open: boolean) => void;
  getAvailableRooms: () => MuseumArea[];
  markDirty: () => void;
  closeAllDropdownsExcept: (openDropdown: WorkDropdown | null) => void;
  onDeleteClick: () => void;
}

/** Onglet « Métadonnées » (Figma admin 323:5421) : titre, statut, lieu, sous-lieu, pastille, ordre. */
export const WorkMetadataFields: React.FC<WorkMetadataFieldsProps> = ({
  isEditMode,
  museum,
  currentLanguage,
  activeLanguages,
  title,
  setTitle,
  statusByLanguage,
  setStatusByLanguage,
  floor,
  setFloor,
  room,
  setRoom,
  pillNumber,
  setPillNumber,
  order,
  setOrder,
  allWorks,
  statusDropdownOpen,
  setStatusDropdownOpen,
  orderDropdownOpen,
  setOrderDropdownOpen,
  floorDropdownOpen,
  setFloorDropdownOpen,
  roomDropdownOpen,
  setRoomDropdownOpen,
  getAvailableRooms,
  markDirty,
  closeAllDropdownsExcept,
  onDeleteClick,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="create-work-section">
        <label htmlFor="work-title-input" className="section-label">
          {t('works.episodeTitle')}
        </label>
        <input
          id="work-title-input"
          type="text"
          className="work-title-field"
          value={title[currentLanguage] || ''}
          onChange={(e) => {
            markDirty();
            setTitle({ ...title, [currentLanguage]: e.target.value });
          }}
        />
      </div>
      <div className="create-work-controls">
        <div className="control-group">
          <label>{t('exhibitions.status')}</label>
          <div className="dropdown">
            <button
              className="dropdown-button"
              onClick={() => {
                closeAllDropdownsExcept(statusDropdownOpen ? null : 'status');
                setStatusDropdownOpen(!statusDropdownOpen);
              }}
            >
              <div className="status-selector-content">
                {statusByLanguage[currentLanguage] === 'draft' ? (
                  <img src={draftIcon} alt="" className="status-icon" />
                ) : (
                  <img src={checkIconCircle} alt="" className="status-icon" />
                )}
                <span>
                  {statusByLanguage[currentLanguage] === 'draft' ? t('works.draft') : t('works.published')}
                </span>
              </div>
              <img
                src={chevronIcon}
                alt=""
                className={`chevron-icon ${statusDropdownOpen ? 'rotated' : ''}`}
              />
            </button>
            {statusDropdownOpen && (
              <div className="dropdown-menu status-dropdown-menu">
                <button
                  className={`status-option ${statusByLanguage[currentLanguage] === 'draft' ? 'active' : ''}`}
                  onClick={() => {
                    markDirty();
                    setStatusByLanguage({
                      ...statusByLanguage,
                      [currentLanguage]: 'draft',
                    });
                    closeAllDropdownsExcept(null);
                  }}
                >
                  <div className="status-selector-content">
                    <img src={draftIcon} alt="" className="status-icon" />
                    <div className="status-text">
                      <span className="status-title">{t('works.draft')}</span>
                      <span className="status-description">{t('works.draftDescription')}</span>
                    </div>
                  </div>
                </button>
                <button
                  className={`status-option ${statusByLanguage[currentLanguage] === 'published' ? 'active' : ''}`}
                  onClick={() => {
                    markDirty();
                    setStatusByLanguage({
                      ...statusByLanguage,
                      [currentLanguage]: 'published',
                    });
                    closeAllDropdownsExcept(null);
                  }}
                >
                  <div className="status-selector-content">
                    <img src={checkIconCircle} alt="" className="status-icon" />
                    <div className="status-text">
                      <span className="status-title">{t('works.published')}</span>
                      <span className="status-description">{t('works.publishedDescription')}</span>
                    </div>
                  </div>
                </button>
                {isEditMode && (
                  <button
                    className="status-option delete-option"
                    type="button"
                    onClick={() => {
                      onDeleteClick();
                      closeAllDropdownsExcept(null);
                    }}
                  >
                    <div className="status-selector-content">
                      <img src={trashIcon} alt="" className="status-icon" />
                      <div className="status-text">
                        <span className="status-title">{t('works.moveToTrash')}</span>
                        <span className="status-description">{t('works.moveToTrashDescription')}</span>
                      </div>
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="control-group">
          <label>{t('works.place')}</label>
          <div className="dropdown">
            <button
              className="dropdown-button"
              onClick={() => {
                closeAllDropdownsExcept(floorDropdownOpen ? null : 'floor');
                setFloorDropdownOpen(!floorDropdownOpen);
              }}
            >
              <span>{getMultilingualValue(floor, DEFAULT_LANGUAGE) || '-'}</span>
              <img src={chevronIcon} alt="" className={`chevron-icon ${floorDropdownOpen ? 'rotated' : ''}`} />
            </button>
            {floorDropdownOpen && (
              <div className="dropdown-menu">
                {museum?.plans?.map((plan, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      const newFloor: Record<ContentLanguageCode, string> = { ...floor };
                      activeLanguages.forEach((lang) => {
                        newFloor[lang] = getMultilingualValueForLang(plan.title, lang) || `Étage ${index + 1}`;
                      });
                      markDirty();
                      setFloor(newFloor);
                      closeAllDropdownsExcept(null);
                    }}
                  >
                    <span>
                      {getMultilingualValueForLang(plan.title, DEFAULT_LANGUAGE) || `Étage ${index + 1}`}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="control-group">
          <label>{t('works.subPlace')}</label>
          <div className="dropdown">
            <button
              className="dropdown-button"
              onClick={() => {
                if (getMultilingualValue(floor, DEFAULT_LANGUAGE)) {
                  closeAllDropdownsExcept(roomDropdownOpen ? null : 'room');
                  setRoomDropdownOpen(!roomDropdownOpen);
                }
              }}
              disabled={!getMultilingualValue(floor, DEFAULT_LANGUAGE)}
            >
              <span>{getMultilingualValue(room, DEFAULT_LANGUAGE) || '-'}</span>
              <img src={chevronIcon} alt="" className={`chevron-icon ${roomDropdownOpen ? 'rotated' : ''}`} />
            </button>
            {roomDropdownOpen && getMultilingualValue(floor, DEFAULT_LANGUAGE) && (
              <div className="dropdown-menu">
                {getAvailableRooms().map((area, index) => {
                  const roomDisplayName =
                    getMultilingualValueForLang(area.title, DEFAULT_LANGUAGE) || `Salle ${index + 1}`;
                  return (
                    <button
                      key={index}
                      onClick={() => {
                        const newRoom: Record<ContentLanguageCode, string> = { ...room };
                        activeLanguages.forEach((lang) => {
                          newRoom[lang] = getMultilingualValueForLang(area.title, lang) || `Salle ${index + 1}`;
                        });
                        markDirty();
                        setRoom(newRoom);
                        closeAllDropdownsExcept(null);
                      }}
                    >
                      <span>{roomDisplayName}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="control-group">
          <label>{t('works.pillNumber')}</label>
          <div className="text-input-wrapper medium-width">
            <img src={headphoneIcon} alt="" className="pill-icon" />
            <input
              type="text"
              className="text-input with-icon"
              value={pillNumber}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  markDirty();
                  setPillNumber(value);
                }
              }}
              placeholder="-"
            />
          </div>
        </div>

        <div className="control-group">
          <label>{t('exhibitions.orderInList')}</label>
          <div className="dropdown">
            <button
              className="dropdown-button medium-width"
              onClick={() => {
                closeAllDropdownsExcept(orderDropdownOpen ? null : 'order');
                setOrderDropdownOpen(!orderDropdownOpen);
              }}
            >
              <span>{order}</span>
              <img src={chevronIcon} alt="" className={`chevron-icon ${orderDropdownOpen ? 'rotated' : ''}`} />
            </button>
            {orderDropdownOpen && (
              <div className="dropdown-menu">
                {Array.from({ length: Math.max(allWorks.length || 1, order) }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    onClick={() => {
                      markDirty();
                      setOrder(num);
                      closeAllDropdownsExcept(null);
                    }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
